/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { callApi } from 'api/helpers/api.helpers';
import _ from 'lodash';
import draftApi from '../../api/draft.api';
import { useAttachmentsConfig } from '../draft/attachments/hooks/useAttachmentsConfig';
import { fileExtension } from '../../shared/enums/fileExtension';
import { downloadFile } from '../../shared/utils/attachmentUtils';
import { specialDocumentTypes } from '../draft/attachments/services/constants';

/**
 *  Custom hook that saves attachments meta information on the state
 *  and exposes methods to extract it from the submission and to load their files.
 */
export default function useAttachments(submission) {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState([]);
  const [attachmentsGenerated, setAttachmentsGenerated] = useState(false);

  const {
    attachmentsConfigReady,
    technicalAttachmentTypes,
    additionalAttachmentTypes,
    paymentAttachmentTypes,
    bioMaterialAttachmentTypes,
    seqListingAttachmentTypes,
    spcAttachmentTypes,
    saAttachmentTypes,
    signatureAttachmentTypes,
  } = useAttachmentsConfig();

  const getCategoryAttachments = (sub, categoryDocumentTypes) => {
    const docTypes = Object.values(categoryDocumentTypes).map(({ type, subType }) => ({ type, subType }));

    return sub.attachments?.filter(
      ({ attachmentType: type, attachmentSubType: subType }) => _.findIndex(docTypes, { type, subType }) !== -1,
    );
  };

  useEffect(() => {
    setAttachmentsGenerated(false);
  }, [submission?.attachments]);

  useEffect(() => {
    (async () => {
      if (attachmentsConfigReady && !attachmentsGenerated && submission) {
        const technicalAttachments = getCategoryAttachments(submission, technicalAttachmentTypes);
        const additionalAttachments = getCategoryAttachments(submission, additionalAttachmentTypes)?.filter(
          document => !!document.attachmentSubType,
        );
        const spcAttachments = getCategoryAttachments(submission, spcAttachmentTypes);
        const paymentAttachments = getCategoryAttachments(submission, paymentAttachmentTypes);
        const bioMaterialAttachments = getCategoryAttachments(submission, bioMaterialAttachmentTypes);
        const seqListAttachments = getCategoryAttachments(submission, seqListingAttachmentTypes);
        const systemGeneratedDocuments = submission.attachments
          ?.filter(document => document.attachmentType === specialDocumentTypes.OTHER)
          .filter(document => document.attachmentSubType === null);
        const saAttachments = getCategoryAttachments(submission, saAttachmentTypes);
        const signatureAttachments = getCategoryAttachments(submission, signatureAttachmentTypes);

        /* eslint-disable array-element-newline */
        setAttachments([
          ...(technicalAttachmentTypes?.length > 0
            ? [
              {
                category: t('technical.documents'),
                files: technicalAttachments,
              },
            ]
            : []),
          ...(additionalAttachmentTypes?.length > 0
            ? [
              {
                category: t('additional.documents'),
                files: additionalAttachments,
              },
            ]
            : []),
          ...(paymentAttachments && paymentAttachments.length > 0
            ? [
              {
                category: t('submission.payment.documents'),
                files: paymentAttachments,
              },
            ]
            : []),
          ...(systemGeneratedDocuments?.length > 0
            ? [
              {
                category: t('system.generated.documents'),
                files: systemGeneratedDocuments,
              },
            ]
            : []),
          ...(seqListAttachments?.length > 0
            ? [
              {
                category: t('attachments.type.SEQ_LIST'),
                files: seqListAttachments,
              },
            ]
            : []),
          ...(spcAttachmentTypes?.length > 0 && spcAttachments?.length > 0
            ? [
              {
                category: t('documents'),
                files: spcAttachments,
              },
            ]
            : []),
          ...(bioMaterialAttachments && bioMaterialAttachments.length > 0
            ? [
              {
                category: t('biologicalInfos.bioMaterial.documents'),
                files: bioMaterialAttachments,
              },
            ]
            : []),
          ...(saAttachments && saAttachments.length > 0
            ? [
              {
                category: t('documents.general'),
                files: saAttachments,
              },
            ]
            : []),
          ...(signatureAttachments && signatureAttachments.length > 0
            ? [
              {
                category: t('submission.signatures'),
                files: signatureAttachments,
              },
            ]
            : []),
        ]);
        setAttachmentsGenerated(true);
      }
    })();
  }, [attachmentsConfigReady, attachmentsGenerated, submission]);

  const loadAttachmentFile = async (file, subm) => {
    const blob = await callApi(draftApi.fetchAttachment)(subm.id, file.id, file.fileName, file.mimeType);

    if (['application/xml', 'text/xml'].includes(file.mimeType)) {
      const fileAsText = await blob.text();
      return { id: file.id, path: fileAsText, type: file.mimeType };
    }

    if (file.fileExtension === fileExtension.ZIP) {
      // returns undefined so that the current preview behavior doesn't trigger
      // (the initial state for previewObject is undefined)
      return downloadFile(URL.createObjectURL(blob), file.originalFileName);
    }
    const fileSrc = URL.createObjectURL(blob);
    return { id: file.id, path: fileSrc, type: file.mimeType };
  };

  return {
    attachments,
    loadAttachmentFile,
    attachmentsConfigReady,
  };
}
